import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";

function flattenTree(zones) {
  const map = new Map();
  zones.forEach(z => map.set(z.id, { ...z, children: [] }));
  const roots = [];
  map.forEach(n => {
    if (n.parent_id && map.has(n.parent_id)) map.get(n.parent_id).children.push(n);
    else roots.push(n);
  });
  const out = [];
  const walk = (nodes, depth) => {
    for (const n of nodes) {
      out.push({ ...n, depth });
      if (n.children.length) walk(n.children, depth + 1);
    }
  };
  walk(roots, 0);
  return out;
}

export default function StructureTab() {
  const [zones, setZones] = useState([]);
  const [newZone, setNewZone] = useState({ name: "", kind: "zona", parent_id: "" });
  const [bulk, setBulk] = useState({ parent_id: "", from_number: 101, to_number: 120, prefix: "" });
  const load = useCallback(() => api.get("/structure/zones").then(r => setZones(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const addZone = async () => {
    if (!newZone.name) return;
    try {
      await api.post("/structure/zones", { name: newZone.name, kind: newZone.kind, parent_id: newZone.parent_id || null });
      setNewZone({ name: "", kind: "zona", parent_id: "" });
      toast.success("Creado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const bulkAdd = async () => {
    if (!bulk.parent_id) { toast.error("Elige un nivel/edificio padre"); return; }
    try {
      const { data } = await api.post("/structure/rooms/bulk", bulk);
      toast.success(data.created + " habitaciones creadas"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar zona y todo lo que contiene?")) return;
    try { await api.delete("/structure/zones/" + id); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const flat = useMemo(() => flattenTree(zones), [zones]);
  const parentOptions = zones.filter(z => z.kind !== "habitacion");
  return (
    <div className="max-w-4xl grid gap-4">
      <div className="q-card p-5">
        <h3 className="font-semibold mb-3">Añadir zona / edificio / nivel / habitación</h3>
        <div className="grid grid-cols-4 gap-2">
          <input placeholder="Nombre" data-testid="new-zone-name" value={newZone.name} onChange={(e) => setNewZone({...newZone, name: e.target.value})} className="h-10 px-3 rounded-lg border border-[color:var(--q-border)] col-span-2"/>
          <select value={newZone.kind} onChange={(e) => setNewZone({...newZone, kind: e.target.value})} className="h-10 px-3 rounded-lg border border-[color:var(--q-border)]">
            <option value="zona">Zona</option>
            <option value="edificio">Edificio</option>
            <option value="nivel">Nivel</option>
            <option value="habitacion">Habitación</option>
          </select>
          <select value={newZone.parent_id} onChange={(e) => setNewZone({...newZone, parent_id: e.target.value})} className="h-10 px-3 rounded-lg border border-[color:var(--q-border)]">
            <option value="">(sin padre)</option>
            {parentOptions.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
        </div>
        <button onClick={addZone} data-testid="add-zone" className="q-btn-primary mt-3">Crear</button>
      </div>

      <div className="q-card p-5">
        <h3 className="font-semibold mb-1">Carga rápida de habitaciones</h3>
        <p className="text-xs text-[color:var(--q-muted)] mb-3">
          Crea muchas habitaciones bajo un nivel/edificio en un solo golpe.
        </p>
        <div className="grid grid-cols-4 gap-2 items-end">
          <div>
            <label className="q-label block mb-1">Padre</label>
            <select data-testid="bulk-parent" value={bulk.parent_id} onChange={(e) => setBulk({...bulk, parent_id: e.target.value})} className="h-10 w-full px-3 rounded-lg border border-[color:var(--q-border)]">
              <option value="">(elige)</option>
              {parentOptions.map(z => <option key={z.id} value={z.id}>{z.name} ({z.kind})</option>)}
            </select>
          </div>
          <div>
            <label className="q-label block mb-1">Prefijo</label>
            <input value={bulk.prefix} onChange={(e) => setBulk({...bulk, prefix: e.target.value})} className="h-10 w-full px-3 rounded-lg border border-[color:var(--q-border)] font-mono"/>
          </div>
          <div>
            <label className="q-label block mb-1">Desde</label>
            <input type="number" data-testid="bulk-from" value={bulk.from_number} onChange={(e) => setBulk({...bulk, from_number: parseInt(e.target.value) || 0})} className="h-10 w-full px-3 rounded-lg border border-[color:var(--q-border)] font-mono"/>
          </div>
          <div>
            <label className="q-label block mb-1">Hasta</label>
            <input type="number" data-testid="bulk-to" value={bulk.to_number} onChange={(e) => setBulk({...bulk, to_number: parseInt(e.target.value) || 0})} className="h-10 w-full px-3 rounded-lg border border-[color:var(--q-border)] font-mono"/>
          </div>
        </div>
        <button data-testid="bulk-submit" onClick={bulkAdd} className="q-btn-gold mt-3">
          Crear {Math.max(0, bulk.to_number - bulk.from_number + 1)} habitaciones
        </button>
      </div>

      <div className="q-card p-5">
        <h3 className="font-semibold mb-3">Estructura actual</h3>
        {flat.length === 0 && <div className="text-sm text-[color:var(--q-muted)]">Sin estructura. Empieza creando una zona o edificio.</div>}
        <div className="grid gap-1">
          {flat.map(n => (
            <div key={n.id} style={{ marginLeft: n.depth * 16 }} className="flex items-center gap-2 py-1 text-sm hover:bg-neutral-50 rounded px-2">
              <span className="q-label text-[color:var(--q-muted)] w-16">{n.kind}</span>
              <span className="font-semibold">{n.name}</span>
              <button onClick={() => del(n.id)} className="ml-auto text-xs text-red-500 hover:underline">borrar</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
